import { MapViewerNode } from '@3ddv/dvm-internal';
import {
  BaseSeatDataTransformer,
  BaseSectionDataTransformer,
  SeatPopoverData,
  SectionPopoverData,
} from '@3ddv/software-division-components';
import { Injectable, inject } from '@angular/core';
import { GeneralAvailability, SeatAvailability } from '@core/models';
import { UtilitiesService } from './utilities.service';

class GroupSalesSeatTransformer extends BaseSeatDataTransformer<SeatAvailability> {
  constructor(private utilitiesService: UtilitiesService) {
    super();
  }

  transform(
    seatAvailability: SeatAvailability,
    context?: { node: MapViewerNode; thumbnail?: string }
  ): SeatPopoverData {
    if (!context) {
      throw new Error(
        'Context with MapViewerNode is required for seat transformation'
      );
    }

    const seatInfo = this.utilitiesService.splittedSeatInfoFromId(
      context.node.original_id
    );
    const primaryPrice = seatAvailability.price?.[0]?.value || 0;

    return {
      id: seatAvailability.id,
      section: seatInfo.section,
      row: seatInfo.seat_row,
      seat: seatInfo.seat,
      price: primaryPrice,
      thumbnail: context.thumbnail,
    };
  }
}

class GroupSalesSectionTransformer extends BaseSectionDataTransformer<GeneralAvailability> {
  transform(
    sectionAvailability: GeneralAvailability,
    context?: {
      node: MapViewerNode;
      thumbnail?: string;
      isSelectedForBA?: boolean;
      priceRangeWithFees?: string;
    }
  ): SectionPopoverData {
    if (!context) {
      throw new Error(
        'Context with MapViewerNode is required for section transformation'
      );
    }

    const firstPrice = sectionAvailability.priceRange?.[0]?.value;
    const secondPrice = sectionAvailability.priceRange?.[1]?.value;
    const min = Number.isInteger(firstPrice)
      ? firstPrice
      : Number(firstPrice.toFixed(2));

    const max = Number.isInteger(secondPrice)
      ? secondPrice
      : Number(secondPrice.toFixed(2));

    const priceRange = {
      min,
      max: max || min || 0,
    };

    const sectionType =
      context.node.type === 'general_admission'
        ? 'general_admission'
        : 'section';

    return {
      priceRange,
      id: sectionAvailability.sectionID,
      section:
        sectionAvailability.mmcID?.replace('S_', '') ||
        sectionAvailability.sectionID,
      availableQuantity: sectionAvailability.availableQuantity,
      adaQuantity: sectionAvailability.availableQuantityAda || 0,
      thumbnail: context.thumbnail,
      type: sectionType,
      isSelectedForBA: context.isSelectedForBA || false,
      priceRangeWithFees: context.priceRangeWithFees,
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class PopoverDataTransformerService {
  private seatTransformer: GroupSalesSeatTransformer;
  private sectionTransformer: GroupSalesSectionTransformer;

  private utilitiesService = inject(UtilitiesService);
  constructor() {
    this.seatTransformer = new GroupSalesSeatTransformer(this.utilitiesService);
    this.sectionTransformer = new GroupSalesSectionTransformer();
  }

  transformSeatData(
    seatAvailability: SeatAvailability,
    node: MapViewerNode,
    thumbnail?: string
  ): SeatPopoverData {
    return this.seatTransformer.transform(seatAvailability, {
      node,
      thumbnail,
    });
  }

  transformSectionData(
    sectionAvailability: GeneralAvailability,
    node: MapViewerNode,
    thumbnail?: string,
    isSelectedForBA: boolean = false,
    priceRangeWithFees?: string
  ): SectionPopoverData {
    return this.sectionTransformer.transform(sectionAvailability, {
      node,
      thumbnail,
      isSelectedForBA,
      priceRangeWithFees,
    });
  }

  getThumbnailSectionId(sectionId: string): string {
    if (sectionId === 'S_BLCHADA') {
      return 'S_Bleacher';
    }
    return sectionId;
  }
}
